/**
 * Iter82 — HR OT Review queue.
 *
 * Lists rows where `detected_ot_minutes > 0 AND authorized_ot_minutes = 0`
 * across a period. Managers can:
 *   • Authorize N minutes at 1.5× (default) via the row action.
 *   • Bulk-authorize multiple rows at the same multiplier.
 *   • Dismiss rows (no OT ever) with a required reason.
 *   • Export the queue as CSV (Iter74.5 RBAC-safe: bank fields never
 *     surface).
 *
 * RBAC (matches Iter61 ROLES_ALLOWED):
 *   Admin / HR Manager / HR Officer / Dept Manager / Attendance Officer.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Download, Zap } from "lucide-react";

import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import useModules from "../hooks/useModules";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import EmptyState from "../components/EmptyState";
import { TableSkeleton } from "../components/SkeletonHelpers";
import { ROLES } from "../lib/rbac";

const ROLES_ALLOWED = new Set([
    ROLES.KAAFI_ADMIN,
    ROLES.KAAFI_HR,
    ROLES.ASL_HR_OFFICER,
    ROLES.ASL_DEPT_MGR,
]);

function humanHM(minutes) {
    const m = Math.max(0, Number(minutes) || 0);
    return `${Math.floor(m / 60)}h ${m % 60}m`;
}

function todayIso() {
    return new Date().toISOString().slice(0, 10);
}

export default function OTReview() {
    const { user } = useAuth();
    const { modules } = useModules();
    const { moduleCode } = useParams();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const canAct = ROLES_ALLOWED.has(user?.role);

    const module = useMemo(() => {
        const code = (moduleCode || "").toUpperCase();
        return (modules || []).find((m) => (m.code || "").toUpperCase() === code) || null;
    }, [modules, moduleCode]);
    const moduleId = module?.id;

    const [dateFrom, setDateFrom] = useState(
        searchParams.get("from") || todayIso(),
    );
    const [dateTo, setDateTo] = useState(
        searchParams.get("to") || todayIso(),
    );
    const [includeDismissed, setIncludeDismissed] = useState(false);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selection, setSelection] = useState(new Set());

    // Row-level dialogs.
    const [authorizeRow, setAuthorizeRow] = useState(null);
    const [dismissRow, setDismissRow] = useState(null);
    // Bulk dialogs.
    const [bulkAuthOpen, setBulkAuthOpen] = useState(false);
    const [bulkDismissOpen, setBulkDismissOpen] = useState(false);

    const load = useCallback(async () => {
        if (!moduleId) return;
        setLoading(true);
        try {
            const { data } = await api.get("/attendance/ot-review", {
                params: {
                    module_id: moduleId,
                    date_from: dateFrom,
                    date_to: dateTo,
                    include_dismissed: includeDismissed,
                },
            });
            setRows(data.items || []);
            setSelection(new Set());
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }, [moduleId, dateFrom, dateTo, includeDismissed]);

    useEffect(() => { load(); }, [load]);

    // URL sync — from/to.
    useEffect(() => {
        const next = new URLSearchParams(searchParams);
        next.set("from", dateFrom);
        next.set("to", dateTo);
        setSearchParams(next, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateFrom, dateTo]);

    const toggleAll = () => {
        setSelection((prev) => {
            if (prev.size === rows.length) return new Set();
            return new Set(rows.map((r) => r.id));
        });
    };
    const toggleRow = (id) => {
        setSelection((prev) => {
            const n = new Set(prev);
            if (n.has(id)) n.delete(id); else n.add(id);
            return n;
        });
    };

    const exportCsv = () => {
        if (!moduleId) return;
        // Build the CSV URL — same params as the list. The endpoint
        // streams a Content-Disposition attachment so the browser
        // triggers a download.
        const p = new URLSearchParams({
            module_id: moduleId,
            date_from: dateFrom,
            date_to: dateTo,
            include_dismissed: includeDismissed ? "true" : "false",
        });
        const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
        const url = `${base}/api/attendance/ot-review.csv?${p.toString()}`;
        window.open(url, "_blank");
    };

    if (!canAct) {
        return (
            <div className="p-8">
                <EmptyState
                    title="Not allowed"
                    subtitle="OT Review is available to Admin / HR / HR Officer / Dept Manager only."
                    testid="ot-review-forbidden"
                />
            </div>
        );
    }

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold asl-navy-text">
                        OT Review — Detected but Unauthorized
                    </h1>
                    <p className="text-sm text-[color:var(--asl-muted)] mt-1">
                        Rows where staff clocked out past shift end but no manager approved the OT.
                        Nothing here is paid until authorized.
                    </p>
                </div>
                <Button
                    variant="outline"
                    onClick={() => navigate(`/m/${moduleCode}/attendance?from=${dateFrom}&to=${dateTo}`)}
                    data-testid="ot-review-back-to-attendance"
                >
                    Back to Attendance
                </Button>
            </div>

            <div
                className="rounded-lg border bg-white p-3 flex items-center gap-3 flex-wrap"
                style={{ borderColor: "var(--asl-border)" }}
            >
                <div>
                    <Label className="text-xs">From</Label>
                    <Input
                        type="date"
                        value={dateFrom}
                        onChange={(e) => setDateFrom(e.target.value)}
                        className="h-8 w-40"
                        data-testid="ot-review-date-from"
                    />
                </div>
                <div>
                    <Label className="text-xs">To</Label>
                    <Input
                        type="date"
                        value={dateTo}
                        onChange={(e) => setDateTo(e.target.value)}
                        className="h-8 w-40"
                        data-testid="ot-review-date-to"
                    />
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer mt-4">
                    <Checkbox
                        checked={includeDismissed}
                        onCheckedChange={(v) => setIncludeDismissed(!!v)}
                        data-testid="ot-review-include-dismissed"
                    />
                    <span>Include dismissed</span>
                </label>
                <div className="ml-auto flex items-center gap-2 mt-4">
                    <span className="text-xs text-[color:var(--asl-muted)]" data-testid="ot-review-count">
                        {rows.length} row{rows.length === 1 ? "" : "s"} · {selection.size} selected
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={exportCsv}
                        disabled={!rows.length}
                        data-testid="ot-review-export-csv"
                    >
                        <Download className="h-4 w-4 mr-1" /> Export CSV
                    </Button>
                    <Button
                        size="sm"
                        className="asl-navy-bg text-white"
                        disabled={selection.size === 0}
                        onClick={() => setBulkAuthOpen(true)}
                        data-testid="ot-review-bulk-authorize"
                    >
                        <Zap className="h-4 w-4 mr-1" /> Authorize selected
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={selection.size === 0}
                        onClick={() => setBulkDismissOpen(true)}
                        className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)]"
                        data-testid="ot-review-bulk-dismiss"
                    >
                        Dismiss selected
                    </Button>
                </div>
            </div>

            <div className="rounded-lg border bg-white overflow-hidden" style={{ borderColor: "var(--asl-border)" }}>
                {loading ? (
                    <TableSkeleton rows={6} cols={6} />
                ) : rows.length === 0 ? (
                    <EmptyState
                        title="No detected but unauthorized OT"
                        subtitle="Every late clock-out has already been reviewed."
                        testid="ot-review-empty"
                    />
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-[color:var(--asl-surface)] text-left text-xs uppercase text-[color:var(--asl-muted)]">
                            <tr>
                                <th className="px-3 py-2 w-8">
                                    <Checkbox
                                        checked={selection.size > 0 && selection.size === rows.length}
                                        onCheckedChange={toggleAll}
                                        data-testid="ot-review-select-all"
                                    />
                                </th>
                                <th className="px-3 py-2">Employee</th>
                                <th className="px-3 py-2">Module ID</th>
                                <th className="px-3 py-2">Date</th>
                                <th className="px-3 py-2">Detected</th>
                                <th className="px-3 py-2">Last clock-out</th>
                                <th className="px-3 py-2">Status</th>
                                <th className="px-3 py-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y" style={{ borderColor: "var(--asl-border)" }}>
                            {rows.map((r) => (
                                <tr key={r.id} data-testid={`ot-review-row-${r.id}`}>
                                    <td className="px-3 py-2">
                                        <Checkbox
                                            checked={selection.has(r.id)}
                                            onCheckedChange={() => toggleRow(r.id)}
                                            data-testid={`ot-review-select-${r.id}`}
                                        />
                                    </td>
                                    <td className="px-3 py-2 font-medium asl-navy-text">
                                        {r.employee_name || r.employee_id}
                                    </td>
                                    <td className="px-3 py-2 mono text-xs">{r.module_employee_id || "—"}</td>
                                    <td className="px-3 py-2 mono text-xs">{r.work_date}</td>
                                    <td className="px-3 py-2 mono text-xs italic text-[color:var(--asl-muted)]">
                                        {humanHM(r.detected_ot_minutes)}
                                    </td>
                                    <td className="px-3 py-2 mono text-xs">
                                        {r.last_clock_out
                                            ? new Date(r.last_clock_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                                            : "—"}
                                    </td>
                                    <td className="px-3 py-2 text-xs">
                                        {r.ot_review_dismissed ? (
                                            <span
                                                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold"
                                                style={{ background: "rgba(107,114,128,0.10)", color: "rgb(75,85,99)" }}
                                            >
                                                Dismissed
                                            </span>
                                        ) : (
                                            <span
                                                className="inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold"
                                                style={{ background: "rgba(168,50,74,0.10)", color: "var(--asl-crimson)" }}
                                            >
                                                Needs review
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                        <div className="flex items-center gap-1 justify-end">
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setAuthorizeRow(r)}
                                                data-testid={`ot-review-authorize-${r.id}`}
                                            >
                                                <Check className="h-3 w-3 mr-1" /> Authorize
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setDismissRow(r)}
                                                data-testid={`ot-review-dismiss-${r.id}`}
                                                className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)]"
                                            >
                                                Dismiss
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {authorizeRow && (
                <AuthorizeDialog
                    row={authorizeRow}
                    onClose={() => setAuthorizeRow(null)}
                    onSaved={() => { setAuthorizeRow(null); load(); }}
                />
            )}
            {dismissRow && (
                <DismissDialog
                    row={dismissRow}
                    onClose={() => setDismissRow(null)}
                    onSaved={() => { setDismissRow(null); load(); }}
                />
            )}
            {bulkAuthOpen && (
                <BulkAuthorizeDialog
                    rowIds={Array.from(selection)}
                    onClose={() => setBulkAuthOpen(false)}
                    onSaved={() => { setBulkAuthOpen(false); load(); }}
                />
            )}
            {bulkDismissOpen && (
                <BulkDismissDialog
                    rowIds={Array.from(selection)}
                    onClose={() => setBulkDismissOpen(false)}
                    onSaved={() => { setBulkDismissOpen(false); load(); }}
                />
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Dialogs (small, single-purpose)
// ---------------------------------------------------------------------------

function AuthorizeDialog({ row, onClose, onSaved }) {
    const defaultMinutes = Number(row.detected_ot_minutes || 0);
    const [hours, setHours] = useState(String(Math.floor(defaultMinutes / 60)));
    const [minutes, setMinutes] = useState(String(defaultMinutes % 60));
    const [multiplier, setMultiplier] = useState("1.5");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const total = useMemo(() => {
        return (Math.max(0, Number(hours) || 0) * 60) + Math.max(0, Number(minutes) || 0);
    }, [hours, minutes]);

    const submit = async () => {
        if (total <= 0) return toast.error("Enter OT hours or minutes (>0).");
        if (!multiplier || Number(multiplier) <= 0) return toast.error("Multiplier is required.");
        if ((reason || "").trim().length < 3) return toast.error("Reason must be at least 3 characters.");
        setSaving(true);
        try {
            await api.post(`/attendance/daily/${row.id}/ot-entries`, {
                minutes: total,
                multiplier: Number(multiplier),
                project_id: row.project_id || null,
                reason: reason.trim(),
            });
            toast.success(`Authorized ${humanHM(total)} at ${multiplier}×`);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="ot-review-authorize-dialog">
                <DialogHeader>
                    <DialogTitle>Authorize OT — {row.employee_name}</DialogTitle>
                    <div className="text-xs text-[color:var(--asl-muted)] mt-1">
                        {row.work_date} · Detected: <span className="italic">{humanHM(defaultMinutes)}</span> from late clock-out.
                    </div>
                </DialogHeader>
                <div className="p-4 space-y-3">
                    <div className="grid grid-cols-3 gap-3">
                        <div>
                            <Label>Hours</Label>
                            <Input type="number" min="0" step="1" value={hours}
                                onChange={(e) => setHours(e.target.value)}
                                data-testid="ot-review-authorize-hours" />
                        </div>
                        <div>
                            <Label>Minutes</Label>
                            <Input type="number" min="0" max="59" step="1" value={minutes}
                                onChange={(e) => setMinutes(e.target.value)}
                                data-testid="ot-review-authorize-minutes" />
                        </div>
                        <div>
                            <Label>Multiplier</Label>
                            <Input type="number" step="0.05" min="0.05" value={multiplier}
                                onChange={(e) => setMultiplier(e.target.value)}
                                data-testid="ot-review-authorize-multiplier" />
                        </div>
                    </div>
                    <div className="text-xs text-[color:var(--asl-muted)]">
                        Total: <span className="asl-navy-text font-medium">{humanHM(total)}</span>
                        {multiplier && Number(multiplier) > 0 ? ` × ${Number(multiplier).toFixed(2)}` : ""}
                    </div>
                    <div>
                        <Label>Reason <span className="asl-crimson-text">*</span></Label>
                        <Textarea rows={2} value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Why is this OT authorized? (min. 3 chars)"
                            data-testid="ot-review-authorize-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="ot-review-authorize-cancel">
                        Cancel
                    </Button>
                    <Button className="asl-navy-bg text-white" onClick={submit} disabled={saving}
                        data-testid="ot-review-authorize-save">
                        {saving ? "Saving…" : "Authorize"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function DismissDialog({ row, onClose, onSaved }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if ((reason || "").trim().length < 3) return toast.error("Reason must be at least 3 characters.");
        setSaving(true);
        try {
            await api.post(`/attendance/ot-review/${row.id}/dismiss`, { reason: reason.trim() });
            toast.success("Dismissed — no OT due");
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="ot-review-dismiss-dialog">
                <DialogHeader>
                    <DialogTitle>Dismiss detected OT — {row.employee_name}</DialogTitle>
                    <div className="text-xs text-[color:var(--asl-muted)] mt-1">
                        {row.work_date} · Detected: <span className="italic">{humanHM(row.detected_ot_minutes)}</span>. No OT will be paid.
                    </div>
                </DialogHeader>
                <div className="p-4 space-y-2">
                    <Label>Reason <span className="asl-crimson-text">*</span></Label>
                    <Textarea rows={3} value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is no OT due? (min. 3 chars)"
                        data-testid="ot-review-dismiss-reason" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="ot-review-dismiss-cancel">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={saving}
                        className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)]"
                        variant="outline" data-testid="ot-review-dismiss-save">
                        {saving ? "Saving…" : "Dismiss"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkAuthorizeDialog({ rowIds, onClose, onSaved }) {
    const [multiplier, setMultiplier] = useState("1.5");
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if (!multiplier || Number(multiplier) <= 0) return toast.error("Multiplier is required.");
        if ((reason || "").trim().length < 3) return toast.error("Reason must be at least 3 characters.");
        setSaving(true);
        try {
            const { data } = await api.post("/attendance/ot-review/bulk-authorize", {
                daily_ids: rowIds,
                multiplier: Number(multiplier),
                reason: reason.trim(),
                minutes_override: null,
            });
            toast.success(`Authorized ${data.authorized_count} row(s)`);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="ot-review-bulk-authorize-dialog">
                <DialogHeader>
                    <DialogTitle>Bulk authorize {rowIds.length} row(s)</DialogTitle>
                    <div className="text-xs text-[color:var(--asl-muted)] mt-1">
                        Each row is authorized for its own detected minutes at the multiplier below.
                    </div>
                </DialogHeader>
                <div className="p-4 space-y-3">
                    <div>
                        <Label>Multiplier</Label>
                        <Input type="number" step="0.05" min="0.05" value={multiplier}
                            onChange={(e) => setMultiplier(e.target.value)}
                            data-testid="ot-review-bulk-authorize-multiplier" />
                    </div>
                    <div>
                        <Label>Reason <span className="asl-crimson-text">*</span></Label>
                        <Textarea rows={2} value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Why is this OT authorized? (min. 3 chars)"
                            data-testid="ot-review-bulk-authorize-reason" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button className="asl-navy-bg text-white" onClick={submit} disabled={saving}
                        data-testid="ot-review-bulk-authorize-save">
                        {saving ? "Saving…" : "Authorize all"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkDismissDialog({ rowIds, onClose, onSaved }) {
    const [reason, setReason] = useState("");
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        if ((reason || "").trim().length < 3) return toast.error("Reason must be at least 3 characters.");
        setSaving(true);
        try {
            const { data } = await api.post("/attendance/ot-review/bulk-dismiss", {
                daily_ids: rowIds,
                reason: reason.trim(),
            });
            toast.success(`Dismissed ${data.dismissed_count} row(s)`);
            onSaved?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };
    return (
        <Dialog open onOpenChange={(v) => !v && onClose?.()}>
            <DialogContent className="max-w-md" data-testid="ot-review-bulk-dismiss-dialog">
                <DialogHeader>
                    <DialogTitle>Bulk dismiss {rowIds.length} row(s)</DialogTitle>
                </DialogHeader>
                <div className="p-4 space-y-2">
                    <Label>Reason <span className="asl-crimson-text">*</span></Label>
                    <Textarea rows={3} value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Why is no OT due? (min. 3 chars)"
                        data-testid="ot-review-bulk-dismiss-reason" />
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button variant="outline" onClick={submit} disabled={saving}
                        className="border-[color:var(--asl-crimson)] text-[color:var(--asl-crimson)]"
                        data-testid="ot-review-bulk-dismiss-save">
                        {saving ? "Saving…" : "Dismiss all"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
